import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { SchoolDTO } from 'src/app/common/dtos/school.dto';
import { translateKpiReportFilterFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import { checkIfB2b, checkIfE2L } from 'src/app/common/utilities/role-helpers';
import {
  KpiReportFilter,
  ReportingPdCreditEarnedItemDTO,
} from '../../../common/dtos/reports.dto';
import { toHoursAndMinutes } from '../../../common/utilities/time-helpers';
import {
  SortEvent,
  SortableHeader,
} from '../../shared/directives/sortable-header.directive';
import { ReportPageFiltersService } from '../../shared/services/report/report-page-filters.service';
import { ReportPdCreditEarnedService } from '../../shared/services/report/report-pd-credit-earned.service';
import { ReportService } from '../../shared/services/report/report.service';

interface CreditEarnedSortIcon {
  title: 'gray' | 'none';
  user_name: 'gray' | 'none';
  cohorts: 'gray' | 'none';
  session_count: 'gray' | 'none';
  competency_count: 'gray' | 'none';
  total_credits_earned: 'gray' | 'none';
}

const BASE_URL = EnvironmentService.apiUrl();

@Component({
  selector: 'app-reporting-credits-earned',
  templateUrl: './reporting-credits-earned.component.html',
  styleUrl: './reporting-credits-earned.component.scss',
  standalone: false,
})
export class ReportingCreditsEarnedComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];

  selectedSchool: SchoolDTO | null;

  selectedDistrict: DistrictSimpleDto | null;

  canFilterByDistrict = false;

  isLoading = false;

  disableDownload = false;

  noResults = false;

  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;

  @ViewChild('p') csvDownloadingPopover: NgbPopover;

  tableData: ReportingPdCreditEarnedItemDTO[];

  listMeta: PaginationDTO;

  filters: KpiReportFilter[];

  sortIconTemp: CreditEarnedSortIcon = {
    title: 'gray',
    user_name: 'gray',
    cohorts: 'gray',
    session_count: 'gray',
    competency_count: 'gray',
    total_credits_earned: 'gray',
  };

  creditTypes: string[] = [];

  sortBy = '';

  sortByDesc = 0;

  column = '';

  direction = '';

  lastUpdatedDate: string;

  constructor(
    private store: Store,
    private reportPdCreditEarnedService: ReportPdCreditEarnedService,
    private reportFiltersService: ReportPageFiltersService,
    private reportService: ReportService
  ) {
    const user = this.store.selectSnapshot(UserState.getUser) as User;
    this.canFilterByDistrict = checkIfE2L(user) || checkIfB2b(user);
  }

  ngOnInit() {
    this.subs.push(
      this.reportFiltersService.filters.pipe().subscribe((filters) => {
        if (filters.schoolYearFilter) {
          this.filters = translateKpiReportFilterFromFilterDTO(filters);
          this.getPdCreditEarnedItems(this.filters);
        }
      })
    );
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }

  downloadCSV(): void {
    this.disableDownload = true;

    this.reportService
      .generateReportCSV('pdSessionCreditsEarnedDownload', this.filters)
      .subscribe({
        error: () => {
          this.csvDownloadingPopover.close();
          this.noResults = true;
          this.disableDownload = false;
        },
        next: (res) => {
          if (res.has_results) {
            // res.file_name should always start with a slash, but need to be sure for the link to work properly
            if (!res.file_name.startsWith('/')) {
              res.file_name = `/${res.file_name}`;
            }
            window.open(`${BASE_URL}${res.file_name}`, '_blank');
          } else {
            this.noResults = true;
          }
          this.disableDownload = false;
        },
      });
  }

  getPdCreditEarnedItems(filters: KpiReportFilter[], page?: number) {
    this.isLoading = true;
    this.lastUpdatedDate = DateTime.now().toFormat('MM/dd h:mm a');
    this.reportPdCreditEarnedService
      .getPdCreditsEarned(filters, page, this.sortBy, this.sortByDesc)
      .subscribe((res) => {
        this.tableData = res.items;

        if (this.tableData.length > 0) {
          this.creditTypes = Object.keys(this.tableData[0].creditTypes);
        }

        this.listMeta = res._meta;
        this.isLoading = false;
      });
  }

  toHoursAndMinutes = toHoursAndMinutes;

  paginationChange() {
    this.getPdCreditEarnedItems(this.filters, this.listMeta.currentPage);
  }

  onSort({ column, direction }: SortEvent) {
    this.headers.forEach((header) => {
      if (header.sortable !== column) {
        header.direction = '';
        this.sortIconTemp[header.sortable as keyof CreditEarnedSortIcon] =
          'gray';
      } else if (header.direction === '') {
        this.sortIconTemp[header.sortable as keyof CreditEarnedSortIcon] =
          'gray';
      } else {
        this.sortIconTemp[header.sortable as keyof CreditEarnedSortIcon] =
          'none';
      }
    });

    this.sortBy = column;
    this.sortByDesc = 0;
    if (direction === 'asc') {
      this.sortByDesc = 1;
    }
    if (direction === '') {
      this.sortBy = '';
    }

    this.column = column;
    this.direction = direction;

    this.getPdCreditEarnedItems(this.filters);
  }
}
